import React, { useState, useEffect } from "react"
import { manageAccountDelete } from "../../../../../api/manage_account_delete"
import { getManageCatvList } from "../../../../../api/manage_catv_list"
import { getManageAccountItem } from "../../../../../api/manage_account_item"
import { useNavigate } from "react-router-dom"
import { ManageCatvData } from "../../../catv/"
import { ManageAccountData } from "../../"
import { useLoading } from "../../../../LoadingContext"

import Card from "@mui/material/Card"
import MDBox from "components/MDBox"
import MDTypography from "components/MDTypography"
import MDInput from "components/MDInput"
import MDButton from "components/MDButton"

import ConfirmPopup from "../../../../ConfirmPopup"

interface ManageCatvDeleteProps {
  onClose: (num: number) => void // モーダルを閉じるためのコールバック
  accountData: ManageAccountData | null // 編集するサービス提供元データをプロパティとして受け取る
}

const ManageAccountCatvDelete: React.FC<ManageCatvDeleteProps> = ({ onClose, accountData }) => {
  const { startLoading, stopLoading } = useLoading()
  const [isPopupOpen, setPopupOpen] = useState(false)
  const navigate = useNavigate()
  const [catvData, setCatvData] = useState<ManageCatvData[]>([])
  const [formData] = useState({
    id: accountData?.id,
    catv_id: accountData?.catv_id,
    name: accountData?.name,
    mail: accountData?.mail,
    auth_account: accountData?.auth_account === 1,
    auth_list: accountData?.auth_list === 1,
    auth_setting: accountData?.auth_setting === 1,
    auth_logo: accountData?.auth_logo === 1
  })

  const [errors, setErrors] = useState({
    id: "",
    catv_id: "",
    name: "",
    mail: "",
    auth_account: "",
    auth_list: "",
    auth_setting: "",
    auth_logo: "",
    server: ""
  })

  useEffect(() => {
    const fetchManageCatvList = async () => {
      try {
        startLoading()
        const manageCatvData = await getManageCatvList()
        setCatvData(manageCatvData.catv_list)
        return manageCatvData.catv_list
      } catch (error: any) {
        if (error.response && error.response.status === 401) {
          navigate("/login") // セッション切れ時にトップページへリダイレクト
        } else if (error.response) {
          const errorDetail = error.response.data.detail
          const validationErrors = { ...errors }
          validationErrors.server = errorDetail.message
          setErrors(validationErrors)
        } else {
          const validationErrors = { ...errors }
          validationErrors.server = "サーバーへの接続に失敗しました"
          setErrors(validationErrors)
        }
      } finally {
        stopLoading()
      }
    }
    fetchManageCatvList()
  }, [])

  const validate = () => {
    const validationErrors = { ...errors }
    validationErrors.server = ""
    setErrors(validationErrors)
    return true
  }

  const convertEnabledValues = (data: any) => {
    return data
  }

  const getManageAccount = async (): Promise<boolean> => {
    try {
      startLoading()
      if (formData.id) {
        const manageAccountData = await getManageAccountItem(formData.id.split("|")[1])
        //console.log("manageAccountData :::: ", manageAccountData)

        return getLoginStatus(manageAccountData.session_date)
      }
    } catch (error: any) {
      if (error.response && error.response.status === 401) {
        navigate("/login") // セッション切れ時にトップページへリダイレクト
      } else if (error.response && error.response.status === 403) {
        const errorDetail = error.response.data.detail
        const validationErrors = { ...errors }
        validationErrors.mail = errorDetail.message
        setErrors(validationErrors)
      } else if (error.response) {
        const errorDetail = error.response.data.detail
        const validationErrors = { ...errors }
        validationErrors.server = errorDetail.message
        setErrors(validationErrors)
      } else {
        const validationErrors = { ...errors }
        validationErrors.server = "サーバーへの接続に失敗しました"
        setErrors(validationErrors)
      }
    } finally {
      stopLoading()
    }
    return false
  }

  const getLoginStatus = (sessionDate: number): boolean => {
    const nowInSeconds = Math.floor(Date.now() / 1000) // 現在時刻を秒単位に変換
    const oneHourInSeconds = 60 * 60 // 1時間を秒単位で表現
    return nowInSeconds - (sessionDate + 9 * 60 * 60) <= oneHourInSeconds ? true : false
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (validate()) {
      //ログイン中かどうか
      if (await getManageAccount()) {
        setPopupOpen(true)
        return
      }

      if (errors.server != "") {
        return
      }

      executeAfterConfirm()
    }
  }

  const executeAfterConfirm = async () => {
    if (validate()) {
      // バリデーション成功時の処理
      try {
        startLoading()
        const convertedData = convertEnabledValues(formData)
        await manageAccountDelete(convertedData.id)
        onClose(1)
      } catch (error: any) {
        if (error.response && error.response.status === 401) {
          navigate("/login") // セッション切れ時にトップページへリダイレクト
        } else if (error.response) {
          const errorDetail = error.response.data.detail
          const validationErrors = { ...errors }
          validationErrors.server = errorDetail.message
          setErrors(validationErrors)
        } else {
          const validationErrors = { ...errors }
          validationErrors.server = "サーバーへの接続に失敗しました"
          setErrors(validationErrors)
        }
      } finally {
        stopLoading()
      }
    }
  }

  // ポップアップのOKボタンが押されたときの処理
  const handleConfirm = async () => {
    setPopupOpen(false) // ポップアップを閉じる
    await executeAfterConfirm() // こっちの処理を実行
  }

  return (
    <div className="modal-bg-cover">
      <Card
        sx={{
          width: "90%",
          maxWidth: "500px",
          maxHeight: "calc(100vh - 100px)",
          marginTop: "50px",
          marginBottom: "50px",
          paddingTop: "20px",
          paddingBottom: "20px",
          paddingLeft: "20px"
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
            padding: "8px 16px",
            boxSizing: "border-box"
          }}
        >
          <MDTypography variant="h4" fontWeight="medium" style={{ flex: 1, textAlign: "left" }}>
            サービス提供元アカウントを削除
          </MDTypography>

          <button
            className="close-button"
            onClick={() => onClose(0)}
            style={{
              position: "absolute",
              right: 16,
              top: 8,
              background: "transparent",
              border: "none",
              fontSize: "24px",
              cursor: "pointer"
            }}
          >
            ×
          </button>
        </div>

        <MDBox style={{ height: "20px" }}></MDBox>
        <MDBox
          style={{
            maxHeight: "calc(100vh - 200px)",
            overflowY: "auto",
            paddingTop: "10px",
            paddingLeft: "5px",
            paddingRight: "25px"
          }}
        >
          <MDBox display="flex" alignItems="center" gap={1} mb={2}>
            <MDInput
              label="アカウントID"
              type="text"
              name="id"
              value={formData.id}
              sx={{ width: "100%" }}
              inputProps={{ readOnly: true }}
            />
          </MDBox>
          <MDBox display="flex" alignItems="center" gap={1} mb={2}>
            <select name="catv_id" value={"Catv|" + formData.catv_id} disabled>
              {catvData.map((catv) => (
                <option key={catv.id} value={catv.id}>
                  {catv.name}
                </option>
              ))}
            </select>
            {errors.catv_id && <p className="error-message">{errors.catv_id}</p>}
          </MDBox>
          <MDBox display="flex" alignItems="center" gap={1} mb={2}>
            <MDInput
              label="名前"
              type="text"
              name="name"
              value={formData.name}
              sx={{ width: "100%" }}
              inputProps={{ readOnly: true }}
            />
          </MDBox>
          {errors.name && (
            <MDBox
              color="#FF0000" // テキスト色
              pl={2} // 左パディング
              mb={2} // 下マージン
              fontSize="0.7em" // フォントサイズ
            >
              {errors.name}
            </MDBox>
          )}
          <MDBox display="flex" alignItems="center" gap={1} mb={2}>
            <MDInput
              label="メールアドレス"
              type="text"
              name="mail"
              value={formData.mail}
              sx={{ width: "100%" }}
              inputProps={{ readOnly: true }}
            />
          </MDBox>
          {errors.mail && (
            <MDBox
              color="#FF0000" // テキスト色
              pl={2} // 左パディング
              mb={2} // 下マージン
              fontSize="0.7em" // フォントサイズ
            >
              {errors.mail}
            </MDBox>
          )}
          {errors.server && (
            <MDBox
              color="#FF0000" // テキスト色
              pl={2} // 左パディング
              mb={2} // 下マージン
              fontSize="0.7em" // フォントサイズ
            >
              {errors.server}
            </MDBox>
          )}
          <MDBox mt={4} mb={2}>
            <MDButton
              variant="contained"
              color="info"
              fullWidth
              sx={{
                borderRadius: "4px",
                textTransform: "none",
                fontSize: "1rem",
                padding: "0.75rem 0",
                backgroundColor: "#1976d2",
                "&:hover": {
                  backgroundColor: "#1565c0"
                }
              }}
              onClick={(e) => {
                handleSubmit(e)
              }}
            >
              削除
            </MDButton>
          </MDBox>
        </MDBox>
      </Card>
      <ConfirmPopup
        isOpen={isPopupOpen}
        message={"削除しようとしているアカウントはログイン中です。\n本当に削除しますか？"}
        buttonName="OK"
        onConfirm={() => {
          handleConfirm()
        }}
        onCancel={() => {
          setPopupOpen(false)
        }}
      />
    </div>
  )
}

export default ManageAccountCatvDelete
